<template>
    <RedesignBaseCounterCard
        :widget="data"
        :card-text="cardText"
        :card-value="cardValue"
        :show-text="showText"
        :display-border="displayBorder"
        :selected-type="SELECTED_TYPE"
        :icon="statusTypeData.icon"
        :icon-color="cardValueIconColorStyles"
        animation
        :limit-status-type="notificationStatus"
        @update:popover-open="popoverOpen = $event"
        class="counter-status-card"
        data-testid="counter-card-status"
    >
        <template
            v-if="cardValue > 0"
            #content-popover
        >
            <RedesignCappedAgentList
                :items="popoverAgentItems"
                :total-count="cardValue"
            />
        </template>
    </RedesignBaseCounterCard>
</template>

<script setup lang="ts">
/* Widget Status Card (Agents in status) */
import { WidgetExtended } from '@/types/dashboard'
import { computed, watch, ref, onBeforeUnmount } from 'vue'
import throttle from 'lodash/throttle'
import { getAllDefaultCardColors } from '@/enum/defaultWidgetSettings'
import {
    currentAccountEntitiesUsers,
    getSelectedAccountStatusTextById,
    selectedAccountData
} from '@/composables/globalData/useEntitiesData'
import { isObject } from '@/util/objectUtil'
import { useI18n } from 'vue-i18n'
import { 
    redesignStatusTypesWithCounterWidgets, 
    otherStatuses, 
    CounterWidgetsOnlyStatuses, 
    Statuses 
} from '@/enum/redesignStatusTypes'
import RedesignBaseCounterCard from '@/components/RedesignComponents/Cards/RedesignBaseCounterCard.vue'
import RedesignCappedAgentList from '@/components/RedesignBaseComponents/RedesignCappedAgentList.vue'
import { CAPPED_AGENT_LIST_MAX_VISIBLE } from '@/components/RedesignBaseComponents/cappedAgentListConfig'
import { removeSoundsFromQueueByIdWithHistoryKeep, updateSoundCurrentValue } from '@/composables/useSoundsStoreData'
import {
    limitThresholdValue,
    useSoundsThresholdNotification,
    warningThresholdValue
} from '@/composables/useSoundsThresholdNotification'
import { Extension } from '@/types/extension'
import useEntityPositiveNegativeFilters from '@/composables/useEntityPositiveNegativeFilters'
import { USER_LIST_PARAMETER_NAME } from '@/enum/widgetTemplateConfigs'
import { allRealtimeExtensions } from '@/composables/useStoreExtensions'

const { t } = useI18n()

type AgentPopoverRow = {
    userID: number
    userName: string
}

const DEFAULT_STATUS = 3
const SELECTED_TYPE = 0
const notificationTag = 'widget.soundBaseCounterCardHtmlRedesign'

const props = withDefaults(
    defineProps<{
        data: WidgetExtended
        editable?: boolean
        onEditMode?: boolean
        showText?: boolean
        displayBorder?: boolean
        queue?: boolean
    }>(),
    {
        showText: true,
        displayBorder: true,
    })

const notificationStatus = ref('')
const popoverOpen = ref(false)

/* Computed */
const cardStatus = computed(() => {
    return props.data?.WidgetLayout?.status ?? DEFAULT_STATUS
})

const cardText = computed(() => {
    return props.data.WidgetLayout.ContentTitle && isObject(props.data.WidgetLayout.ContentTitle) ? props.data.WidgetLayout.ContentTitle[cardStatus.value] : defaultCardText.value.toString()
})

const widgetData = computed(() => {
    return props.data
})

const isCounterWidgetOnlyStatus = computed(() => {
    return (Object.values(CounterWidgetsOnlyStatuses) as number[]).includes(cardStatus.value)
})

const defaultCardText = computed(() => {
    if (isCounterWidgetOnlyStatus.value) {
        return t(statusTypeData.value?.text ?? '')
    }
    return t(getSelectedAccountStatusTextById(cardStatus.value))
})

const { filteredItems: filteredExtensions } = useEntityPositiveNegativeFilters(
    widgetData,
    USER_LIST_PARAMETER_NAME,
    allRealtimeExtensions,
    'userID'
)

const filteredExtensionsSummary = computed(() =>
    collectMatchingUsers(
        filteredExtensions.value,
        cardStatus.value,
        popoverOpen.value ? CAPPED_AGENT_LIST_MAX_VISIBLE : 0
    )
)

const cardValue = computed(() => filteredExtensionsSummary.value.total)

const popoverAgentItems = computed(() =>
    filteredExtensionsSummary.value.sample.map(row => ({
        id: `${row.userID}_${props.data.WidgetID}`,
        name: row.userName
    }))
)

const statusTypeData = computed(() => {
    return redesignStatusTypesWithCounterWidgets.value[cardStatus.value]
})
const cardValueIconColorStyles = computed(() => {
    const defaultColor = props.data.WidgetLayout?.colors?.fonts || getAllDefaultCardColors().fonts
    return cardStatus.value ? styleForStatusIconColor(cardStatus.value) : defaultColor
})

/* Methods */
function hasAnsweredCallWithDirection (el: Extension, direction: 'outgoing' | 'incoming'): boolean {
    if (!el.calls?.length) return false

    return el.calls.some(call => {
        if (!call.answered) return false
        const callDir = call.direction || call.callType || ''
        return direction === 'incoming'
            ? callDir === 'Incoming'
            : callDir !== 'Incoming'
    })
}

function extensionMatchesStatus (
    el: Extension,
    status: number,
    isAtWork: boolean
): boolean {
    if (isAtWork) {
        return el.representativeStatus !== Statuses.LOGOUT
    }
    if (status === CounterWidgetsOnlyStatuses.IN_CALL_OUTGOING) {
        return el.representativeStatus === Statuses.IN_CALL
            && hasAnsweredCallWithDirection(el, 'outgoing')
    }
    if (status === CounterWidgetsOnlyStatuses.IN_CALL_INCOMING) {
        return el.representativeStatus === Statuses.IN_CALL
            && hasAnsweredCallWithDirection(el, 'incoming')
    }
    return el.representativeStatus === status
}

function collectMatchingUsers (
    extensions: Extension[],
    status: number,
    sampleLimit: number
) {
    const isAtWork = status === otherStatuses.AT_WORK

    return extensions.reduce<{ total: number, sample: AgentPopoverRow[] }>(
        (acc, extension) => {
            if (!currentAccountEntitiesUsers.value?.[extension.userID] || !extensionMatchesStatus(extension, status, isAtWork)) {
                return acc
            }
            acc.total++
            if (acc.sample.length < sampleLimit) {
                acc.sample.push({
                    userID: extension.userID,
                    userName: extension.userName
                })
            }
            return acc
        },
        {
            total: 0,
            sample: []
        }
    )
}

const {
    getCurrentNotificationStatus,
    setupNotification,
    getThresholdValue,
    reverseThreshold
} = useSoundsThresholdNotification(widgetData)

function styleForStatusIconColor (status: number) {
    const dynamicColor = selectedAccountData.value?.AccountBreaks.find(el => Number(el.StatusID) === Number(status))
    return dynamicColor && dynamicColor.ColorCode ? dynamicColor.ColorCode : statusTypeData.value?.color
}

const updateNotification = (val: number, type: string, id: string) => {
    const notificationData = {
        notification_value: getThresholdValue(type),
        entity_name: cardText.value,
        current_value: `${cardValue.value}.`,
        property: '',
        type: type
    }
    const notificationHtml =  t(notificationTag, { ...notificationData })
    updateSoundCurrentValue(id, notificationHtml)
}

const evaluateNotificationStatus = () => {
    const val = cardValue.value
    const currentNotificationStatus = getCurrentNotificationStatus(val, reverseThreshold.value)

    if (!currentNotificationStatus && !notificationStatus.value) return
    if (!currentNotificationStatus) {
        notificationStatus.value = ''
        removeSoundsFromQueueByIdWithHistoryKeep(`${props.data.WidgetID}`)
        return
    }
    if(currentNotificationStatus === notificationStatus.value) {
        updateNotification(val, currentNotificationStatus, `${props.data.WidgetID}`)
        return
    }
    removeSoundsFromQueueByIdWithHistoryKeep(`${props.data.WidgetID}`)
    const shouldSoundNotBePlayed = notificationStatus.value === limitThresholdValue && currentNotificationStatus === warningThresholdValue
    notificationStatus.value = currentNotificationStatus

    setupNotification({
        entityName: cardText.value,
        property: '',
        soundPlayed: shouldSoundNotBePlayed,
        currentValue: `${cardValue.value}.`,
        id: `${props.data.WidgetID}`,
        notificationTag,
        type: currentNotificationStatus,
    })
}

const scheduleNotificationEvaluation = throttle(evaluateNotificationStatus, 1000)

onBeforeUnmount(() => {
    scheduleNotificationEvaluation.cancel()
})

watch(() => cardValue.value, () => {
    scheduleNotificationEvaluation()
}, { immediate: true })

watch(() => widgetData.value, () => {
    scheduleNotificationEvaluation()
})

</script>
